// admin/socket/index.ts — singleton socket.io client.
//
// One Socket per admin session. Constructed from createSocket(token) when
// the AdminApp mounts and disposed when the user signs out or token changes.
//
// Cursor persistence lives here too — every received event updates
// localStorage[`ember.admin.cursor.<adminId>`] with the event's `at` so the
// reconnect path's sync:catchup can supply it.

import { io, type Socket } from "socket.io-client";
import type {
  AdminCatchupAck,
  AdminCatchupRequest,
  AdminEvent,
  AdminSync,
} from "../store/types";

const API_BASE: string = import.meta.env.VITE_API_BASE || "";

const CURSOR_PREFIX = "ember.admin.cursor.";

export function loadCursor(adminId: string): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(`${CURSOR_PREFIX}${adminId}`);
  } catch {
    return null;
  }
}

export function saveCursor(adminId: string, cursor: string): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(`${CURSOR_PREFIX}${adminId}`, cursor);
  } catch {
    // ignore — localStorage may be disabled / full
  }
}

export function clearCursor(adminId: string): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(`${CURSOR_PREFIX}${adminId}`);
  } catch {
    // ignore
  }
}

/**
 * Build a socket and return it. The caller is responsible for:
 *   1. Wiring `connect`, `connect_error`, `disconnect`, `event`, `sync:initial`
 *      handlers (typically via the reducerBridge).
 *   2. Calling `disconnect()` on unmount.
 */
export function createSocket(token: string): Socket {
  // Same origin as /api/* — when API_BASE is empty (dev with Vite proxy), the
  // socket connects to window.location.origin. When API_BASE is set in
  // production it's the absolute backend URL.
  const target = API_BASE || (typeof window !== "undefined" ? window.location.origin : "");

  return io(target, {
    auth: { token },
    // Both transports — websocket primary, polling fallback for proxies that
    // mangle WS upgrades. socket.io's default already includes polling, kept
    // explicit for visibility.
    transports: ["websocket", "polling"],
    // socket.io auto-reconnects by default; tighten the gap so a brief
    // network blip surfaces fast.
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    // Don't auto-connect on construction — give the caller a chance to wire
    // listeners first.
    autoConnect: false,
  });
}

/** Promise-wrap a sync:catchup emit. Resolves with the ack payload. */
export function emitCatchup(
  socket: Socket,
  request: AdminCatchupRequest,
): Promise<AdminCatchupAck> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error("sync:catchup timed out"));
    }, 10_000);

    socket.emit("sync:catchup", request, (ack: AdminCatchupAck) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      resolve(ack);
    });
  });
}

// Re-export types so importers don't have to traverse to store/types directly.
export type { AdminEvent, AdminSync };
